import { create } from 'zustand';
import type { CompanyWorkspace, WorkspaceContract } from '@nest-react/domain';

import {
  type AgentReferencesSlice,
  createAgentReferencesSlice,
} from './slices/agentReferencesSlice';
import {
  type AgentComposerSessionSlice,
  createAgentComposerSessionSlice,
} from './slices/agentComposerSessionSlice';
import {
  type AgentSessionsSlice,
  createAgentSessionsSlice,
} from './slices/agentSessionsSlice';
import { type AssetsSlice, createAssetsSlice } from './slices/assetsSlice';
import { type BillingSlice, createBillingSlice } from './slices/billingSlice';
import {
  type IdentitySlice,
  createIdentitySlice,
} from './slices/identitySlice';
import { emptyIdentitySnapshot } from './types';

export type WorkspaceStore = IdentitySlice &
  AssetsSlice &
  BillingSlice &
  AgentSessionsSlice &
  AgentReferencesSlice &
  AgentComposerSessionSlice;

export const useWorkspaceStore = create<WorkspaceStore>()((...args) => ({
  ...createIdentitySlice(...args),
  ...createAssetsSlice(...args),
  ...createBillingSlice(...args),
  ...createAgentSessionsSlice(...args),
  ...createAgentReferencesSlice(...args),
  ...createAgentComposerSessionSlice(...args),
}));

export const selectActiveWorkspaceId = (state: WorkspaceStore): string | null =>
  state.identity.activeWorkspaceId;

export const selectActiveWorkspace = (
  state: WorkspaceStore
): WorkspaceContract | null => {
  const { activeWorkspaceId, workspaceContracts } = state.identity;
  if (activeWorkspaceId) {
    return (
      workspaceContracts.find(item => item.id === activeWorkspaceId) ?? null
    );
  }
  return workspaceContracts.length === 1 ? workspaceContracts[0] : null;
};

export const selectActiveCompany = (
  state: WorkspaceStore
): CompanyWorkspace | null => {
  const activeWorkspace = selectActiveWorkspace(state);
  if (!activeWorkspace) return null;
  return (
    state.identity.companies.find(
      company => company.id === activeWorkspace.id
    ) ?? null
  );
};

export const selectWorkspaceLocale = (
  state: WorkspaceStore
): WorkspaceStore['identity']['locale'] => state.identity.locale;

export const selectWorkspaceAssets = (
  state: WorkspaceStore,
  workspaceId: string
): WorkspaceStore['assetsByWorkspace'][string] =>
  state.assetsByWorkspace[workspaceId];

export const selectWorkspaceWallet = (
  state: WorkspaceStore,
  workspaceId: string
): WorkspaceStore['billingByWorkspace'][string] =>
  state.billingByWorkspace[workspaceId];

export const selectWorkspaceAgentSessions = (
  state: WorkspaceStore,
  workspaceId: string
): WorkspaceStore['agentSessionsByWorkspace'][string] =>
  state.agentSessionsByWorkspace[workspaceId];

export const selectWorkspaceAgentReferences = (
  state: WorkspaceStore,
  workspaceId: string
): WorkspaceStore['agentReferencesByWorkspace'][string] =>
  state.agentReferencesByWorkspace[workspaceId];

export const selectAgentComposerSession = (
  state: WorkspaceStore,
  key: string
): WorkspaceStore['agentComposerByKey'][string] =>
  state.agentComposerByKey[key];

export const resetWorkspaceStore = (): void => {
  useWorkspaceStore.setState(state => ({
    ...state,
    identity: emptyIdentitySnapshot(),
    assetsByWorkspace: {},
    billingByWorkspace: {},
    agentSessionsByWorkspace: {},
    agentReferencesByWorkspace: {},
    agentComposerByKey: {},
  }));
};
